import { useQuery } from '@tanstack/react-query';

import {
  type Team,
  type TeamBilling,
  TeamBillingSchema,
  TeamSchema,
  type Teams,
  type TeamsQuery,
  TeamsSchema,
  type UpdateTeamRequest,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const TEAMS_QUERY_KEY = 'teams';

export const useTeamQuery = (teamId: string | undefined | null) => {
  return useQuery({
    queryKey: [TEAMS_QUERY_KEY, teamId],
    queryFn: async (): Promise<Team | null> => {
      if (!teamId) return null;
      const response = await apiClient.get<Team>(`/teams/${teamId}`);

      if (response.status === 404) {
        return null;
      }

      if (response.status !== 200) {
        logger.error('failed to fetch team', {
          teamId,
          status: response.status,
        });
        throw new Error('failed to fetch team');
      }

      const validatedData = TeamSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate team data', {
          teamId,
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!teamId,
  });
};

export const useTeamsQuery = (params: TeamsQuery, enabled: boolean) => {
  return useQuery({
    queryKey: [TEAMS_QUERY_KEY, params],
    queryFn: async (): Promise<Teams> => {
      const response = await apiClient.get<Team[]>('/teams', { params });
      const validatedData = TeamsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate teams data', {
          params,
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const updateTeam = async (
  teamId: string,
  options: UpdateTeamRequest,
) => {
  const response = await apiClient.put<Team>(`/teams/${teamId}`, options);
  const validatedData = TeamSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate team data', {
      teamId,
      error: validatedData.error,
    });
    return response.data;
  }
  return validatedData.data;
};

export const useTeamBillingQuery = (teamId: string) => {
  return useQuery({
    queryKey: [TEAMS_QUERY_KEY, teamId, 'billing'],
    queryFn: async (): Promise<TeamBilling> => {
      const response = await apiClient.get<TeamBilling>(
        `/teams/${teamId}/billing`,
      );
      const validatedData = TeamBillingSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate team billing data', {
          teamId,
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
  });
};
